import type { HrSample, HrZone, HrZoneId } from '@/lib/heartRateZones';
import { classifyBpm } from '@/lib/heartRateZones';

export const HR_ZONE_STROKE_COLORS: Record<HrZoneId, string> = {
  z1: '#60a5fa',
  z2: '#34d399',
  z3: '#fbbf24',
  z4: '#fb923c',
  z5: '#f87171',
};

export type RoutePointWithTime = { lat: number; lng: number; timestamp?: number };

export type ColoredRouteSegment = {
  points: [number, number][];
  color: string;
  zone: HrZoneId | null;
};

const bpmAtTime = (samples: HrSample[], timestamp: number): number | null => {
  if (samples.length === 0) return null;
  let best: HrSample | null = null;
  let bestDelta = Infinity;
  for (const s of samples) {
    const delta = Math.abs(s.timestamp - timestamp);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = s;
    }
  }
  if (!best || bestDelta > 90_000) return null;
  return best.bpm;
};

/** Split GPS route into MapKit segments colored by nearest heart-rate zone. */
export function buildZoneColoredRouteSegments(
  routePoints: RoutePointWithTime[],
  hrSamples: HrSample[],
  zones: HrZone[],
): ColoredRouteSegment[] {
  if (routePoints.length < 2 || hrSamples.length === 0) return [];

  const sortedHr = [...hrSamples].sort((a, b) => a.timestamp - b.timestamp);
  const out: ColoredRouteSegment[] = [];

  let currentZone: HrZoneId | null = null;
  let currentPoints: [number, number][] = [];

  const flush = () => {
    if (currentPoints.length >= 2) {
      out.push({
        points: [...currentPoints],
        color: currentZone ? HR_ZONE_STROKE_COLORS[currentZone] : '#0bd9a8',
        zone: currentZone,
      });
    }
    currentPoints = currentPoints.length > 0 ? [currentPoints[currentPoints.length - 1]] : [];
  };

  for (let i = 0; i < routePoints.length; i++) {
    const p = routePoints[i];
    const pt: [number, number] = [p.lat, p.lng];
    const ts =
      p.timestamp ??
      (sortedHr[0].timestamp +
        (i / Math.max(1, routePoints.length - 1)) *
          (sortedHr[sortedHr.length - 1].timestamp - sortedHr[0].timestamp));

    const bpm = bpmAtTime(sortedHr, ts);
    const zone = bpm != null ? classifyBpm(Math.round(bpm), zones) : null;

    if (i === 0) {
      currentZone = zone;
      currentPoints.push(pt);
      continue;
    }

    if (zone !== currentZone) {
      currentPoints.push(pt);
      flush();
      currentZone = zone;
      currentPoints.push(pt);
    } else {
      currentPoints.push(pt);
    }
  }

  flush();
  return out.filter((seg) => seg.points.length >= 2);
};
